/**
 * DechargePublique — Page publique accessible par lien (sans auth)
 *
 * 🆕 [Phase 89 — 2026-07-07] Refonte mobile-first complète (pattern Phase 77/78) :
 *   - Anti dark mode Samsung : 3 verrous (colorScheme:'light only' + meta
 *     theme-color + classes bg-white text-slate-900 explicites)
 *   - Layout responsive Tailwind : container `max-w-3xl mx-auto`, padding
 *     `p-4 sm:p-6 md:p-8`, typo `text-base` (≥16px pour bloquer zoom iOS)
 *   - Header entreprise/client : grid 1-col mobile → 2-col desktop
 *   - CTA « Signer » : `min-h-[48px]` full-width mobile pour cible tactile Apple HIG
 *   - Fallback logo si asset absent (nom PRQC en vert emerald-700, 3 lignes)
 *   - Nouveau bouton « Télécharger en PDF » (desktop top + mobile sticky-bottom)
 *     avec html2pdf.js `scale: isMobile ? 1.5 : 2 + windowWidth: 1024`
 *     + fallback `window.print()` si html2pdf échoue
 *   - Masquage du badge « Made with Emergent » (uniquement sur cette page publique)
 *   - Signature : canvas conservé (Phase 79 pattern), touch-none, hi-res via
 *     `devicePixelRatio` pour rendu net sur mobile
 */
import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usePublicFetch } from '../hooks/usePublicFetch';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || process.env.REACT_APP_BACKEND_URL || '';

export default function DechargePublique() {
  const { token } = useParams();
  const { data, loading, error, refetch } = usePublicFetch(
    `/api/legal/public/decharge/${token}`,
    { notFoundMsg: "Cette decharge n'existe pas ou le lien est invalide." }
  );
  const [showSignature, setShowSignature] = useState(false);
  const [signatureNom, setSignatureNom] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const canvasRef = useRef(null);
  const pdfRootRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const signatureSectionRef = useRef(null);

  // 🆕 [Phase 89] Anti dark mode Samsung — injecte <meta name="theme-color">
  // pour forcer la chrome bar claire et signaler OneUI/Force Dark de ne pas
  // inverser les couleurs.
  useEffect(() => {
    const existing = document.querySelector('meta[name="theme-color"]');
    const created = !existing;
    let meta = existing;
    if (created) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    const previousContent = meta.getAttribute('content');
    meta.setAttribute('content', '#ffffff');
    return () => {
      if (created) {
        document.head.removeChild(meta);
      } else if (previousContent !== null) {
        meta.setAttribute('content', previousContent);
      }
    };
  }, []);

  useEffect(() => {
    if (data?.decharge?.signature_obtenue) setSigned(true);
  }, [data]);

  // 🆕 [Phase 89] Canvas hi-res (Phase 79 pattern) — scale par devicePixelRatio
  // pour éviter le flou sur écrans Retina / haute densité mobile.
  useEffect(() => {
    if (showSignature && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      // Set backing store size for retina
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, rect.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [showSignature]);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    e.preventDefault?.();
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault?.();
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoords(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    const rect = c.getBoundingClientRect();
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
  };

  const handleClickSigner = () => {
    setShowSignature(true);
    setTimeout(() => {
      signatureSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSign = async () => {
    if (!signatureNom.trim()) { toast.error('Veuillez entrer votre nom'); return; }
    const canvas = canvasRef.current; if (!canvas) return;
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    if (!imageData.data.some((p, i) => i % 4 !== 3 && p !== 255)) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }
    setSigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/legal/public/decharge/${token}/signer`, {
        signature_data: canvas.toDataURL('image/png'),
        signature_nom: signatureNom.trim()
      });
      if (res.status !== 200) {
        const err = res.data || {};
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Décharge signée avec succès!');
      setSigned(true);
      setShowSignature(false);
      refetch();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally {
      setSigning(false);
    }
  };

  // 🆕 [Phase 89] Téléchargement PDF (pattern Phase 77)
  const handleDownloadPdf = async () => {
    if (!pdfRootRef.current || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const filename = `Decharge_${(data?.dossier?.client_nom || 'Signee').replace(/[^a-z0-9\-_]/gi, '_')}.pdf`;
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      await html2pdf()
        .from(pdfRootRef.current)
        .set({
          margin: [10, 10, 10, 10],
          filename,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: isMobile ? 1.5 : 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: 1024,
          },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .save();
    } catch (e) {
      console.error('[Phase 89] Erreur html2pdf:', e);
      const useFallback = window.confirm(
        "Le téléchargement PDF n'a pas pu être généré sur ce navigateur.\n\n" +
        "Voulez-vous utiliser l'impression du navigateur (vous pourrez choisir « Enregistrer en PDF » dans la fenêtre d'impression) ?"
      );
      if (useFallback) {
        try { window.print(); } catch (_e) {
          alert("L'impression n'a pas pu être démarrée. Veuillez télécharger depuis un ordinateur.");
        }
      }
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-900"
      style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
    >
      <div className="text-center">
        <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-3" />
        <p className="text-slate-500 text-sm">Chargement de la décharge...</p>
      </div>
    </div>
  );

  if (error) return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-900 p-4"
      style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
    >
      <div className="bg-white rounded-xl p-8 text-center shadow-sm max-w-md w-full">
        <p className="text-lg font-semibold text-red-600">{error}</p>
      </div>
    </div>
  );

  const { decharge, dossier, entreprise } = data || {};

  // 🆕 [Phase 89] Logo URL — supporte plusieurs sources (Phase 77 pattern)
  const logoUrl = entreprise?.logo_url
    || (entreprise?.logo_principal ? `${API_URL}${entreprise.logo_principal}` : null)
    || (entreprise?.logos?.[0]?.id ? `${API_URL}/api/entreprise/logos/${entreprise.logos[0].id}` : null);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: letter; margin: 0.5in; }
          html, body { height: auto !important; overflow: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; }
          #root { position: static !important; overflow: visible !important; height: auto !important; }
          .decharge-wrapper { min-height: auto !important; background: white !important; padding: 0 !important; }
          .decharge-card { box-shadow: none !important; border-radius: 0 !important; max-width: none !important; }
          div[style*="position: fixed"], div[style*="position:fixed"],
          [data-emergent-badge], #emergent-badge, .emergent-badge,
          a[href*="emergentagent"], a[href*="emergent"] {
            display: none !important; visibility: hidden !important;
          }
        }
        /* 🆕 [Phase 89] Masquage du badge Emergent uniquement sur cette page
           publique (respecte les ToS Emergent sur pages internes admin). */
        #emergent-badge,
        [id^="emergent-"],
        [data-emergent-badge],
        a[href*="emergentagent.com"],
        a[href*="emergent.sh"],
        a[href*="app.emergent.sh"] {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `}</style>
      {/* 🆕 [Phase 89] Root wrapper — anti dark mode Samsung */}
      <div
        className="decharge-wrapper min-h-screen bg-slate-100 text-slate-900 px-3 py-5 sm:px-4 sm:py-6 md:py-8 pb-24 md:pb-8"
        style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
        data-testid="decharge-publique-root"
      >
        {/* Barre du haut — Télécharger PDF (desktop) */}
        <div className="no-print max-w-3xl mx-auto mb-3 hidden md:flex justify-end gap-2">
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="btn-download-pdf-desktop"
            className={`px-5 py-2 text-white text-sm font-semibold rounded-lg border-none transition shadow-sm ${
              downloadingPdf
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 cursor-pointer'
            }`}
          >
            {downloadingPdf ? 'Génération…' : '📥 Télécharger en PDF'}
          </button>
        </div>

        {/* Card document */}
        <div
          ref={pdfRootRef}
          className="decharge-card max-w-3xl mx-auto bg-white shadow-sm rounded-lg md:rounded-xl overflow-hidden"
          data-testid="pdf-content-root"
        >
          <div className="p-4 sm:p-6 md:p-8 space-y-4 md:space-y-5 text-base">
            {/* HEADER — Logo entreprise + Client (grid 1 col mobile → 2 cols desktop) */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-testid="decharge-header">
              <div className="text-center md:text-left">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    className="h-16 sm:h-20 object-contain mx-auto md:mx-0"
                    data-testid="decharge-logo-img"
                    onError={(e) => {
                      // 🆕 [Phase 89] Fallback si le logo échoue à charger
                      e.target.style.display = 'none';
                      const fallback = e.target.parentNode.querySelector('[data-testid="decharge-logo-fallback"]');
                      if (fallback) fallback.style.display = 'block';
                    }}
                  />
                ) : null}
                <div
                  className={`text-xl sm:text-2xl md:text-3xl font-bold text-emerald-700 leading-tight ${logoUrl ? 'hidden' : ''}`}
                  data-testid="decharge-logo-fallback"
                  style={logoUrl ? { display: 'none' } : {}}
                >
                  Peinture<br />Résidentielle<br />Qc Inc.
                </div>
                <h2 className="font-bold text-base sm:text-lg mt-2">
                  {entreprise?.nom_entreprise || 'Peinture Résidentielle QC'}
                </h2>
                <p className="text-sm text-slate-600">{entreprise?.adresse}</p>
                {entreprise?.telephone && (
                  <p className="text-sm text-slate-600">
                    <a href={`tel:${entreprise.telephone}`} className="text-slate-700 hover:text-blue-600 no-underline">
                      {entreprise.telephone}
                    </a>
                  </p>
                )}
              </div>
              <div className="text-center md:text-right border-t md:border-t-0 md:border-l border-slate-200 pt-4 md:pt-0 md:pl-6">
                <p className="text-sm text-slate-500 font-medium">Client</p>
                <p className="font-semibold text-base sm:text-lg mt-1">{dossier?.client_nom}</p>
                <p className="text-sm text-slate-600 mt-1">{dossier?.adresse_travaux}</p>
                <p className="text-sm text-slate-600">{dossier?.ville_travaux}</p>
              </div>
            </section>

            {/* Titre principal — mobile-first typo */}
            <h1
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-[#1a237e] pt-2"
              data-testid="decharge-titre"
            >
              DÉCHARGE ET QUITTANCE
            </h1>

            {/* Contenu de la décharge */}
            <div
              className="bg-slate-50 rounded-lg p-4 sm:p-5 md:p-6"
              data-testid="decharge-contenu-wrapper"
            >
              <div
                className="prose prose-sm sm:prose max-w-none text-slate-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: decharge?.contenu }}
              />
            </div>

            {/* Statut signature */}
            {signed ? (
              <div
                className="bg-green-50 border border-green-200 rounded-xl p-5 sm:p-6 text-center"
                data-testid="decharge-signed"
              >
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-800 font-bold text-lg">Décharge signée</p>
                <p className="text-green-700 text-sm mt-1">
                  Signé par {decharge?.signature_client_nom} le {decharge?.date_signature ? new Date(decharge.date_signature).toLocaleDateString('fr-CA') : '—'}
                </p>
                {decharge?.signature_client_data && (
                  <div className="mt-4">
                    <img
                      src={decharge.signature_client_data}
                      alt="Signature"
                      className="h-16 mx-auto"
                      data-testid="decharge-signature-image"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center pt-2">
                <button
                  onClick={handleClickSigner}
                  className="w-full sm:w-auto min-h-[48px] px-8 py-3 bg-[#1a237e] text-white rounded-xl font-bold text-base sm:text-lg hover:bg-[#0d1854] active:bg-[#0d1854] transition-colors shadow-lg touch-manipulation"
                  data-testid="btn-signer-decharge"
                >
                  Signer la décharge
                </button>
              </div>
            )}

            {/* Zone de signature */}
            {showSignature && !signed && (
              <div
                ref={signatureSectionRef}
                className="mt-4 border-t border-slate-200 pt-6 space-y-4"
                data-testid="decharge-signature-zone"
              >
                <div>
                  <label
                    htmlFor="signature-nom"
                    className="block text-sm font-medium text-slate-700 mb-1.5"
                  >
                    Votre nom complet
                  </label>
                  <input
                    id="signature-nom"
                    type="text"
                    value={signatureNom}
                    onChange={(e) => setSignatureNom(e.target.value)}
                    className="w-full min-h-[44px] px-4 py-2 text-base border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-slate-900"
                    placeholder="Prénom et nom"
                    data-testid="input-signature-nom"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Votre signature
                  </label>
                  <canvas
                    ref={canvasRef}
                    className="w-full h-40 sm:h-48 border-2 border-slate-300 rounded-lg bg-white cursor-crosshair touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="canvas-signature"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={clearSignature}
                      className="min-h-[40px] px-4 py-2 text-sm bg-slate-100 rounded-lg hover:bg-slate-200 active:bg-slate-300 transition-colors touch-manipulation"
                      data-testid="btn-clear-signature"
                    >
                      Effacer
                    </button>
                  </div>
                </div>
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="w-full min-h-[48px] py-3 bg-green-600 text-white rounded-xl font-bold text-base hover:bg-green-700 active:bg-green-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 touch-manipulation"
                  data-testid="btn-confirm-signature"
                >
                  {signing
                    ? <><Loader2 className="w-5 h-5 animate-spin" /> Signature en cours...</>
                    : 'Confirmer ma signature'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 🆕 [Phase 89] CTA mobile sticky-bottom — Télécharger PDF (mobile only) */}
        <div
          className="no-print md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 px-3 py-3 shadow-lg"
          data-testid="mobile-sticky-actions"
        >
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="btn-download-pdf-mobile"
            className={`w-full min-h-[48px] text-white text-base font-semibold rounded-lg border-none transition shadow-sm touch-manipulation ${
              downloadingPdf
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 cursor-pointer'
            }`}
          >
            {downloadingPdf ? 'Génération…' : '📥 Télécharger en PDF'}
          </button>
        </div>
      </div>
    </>
  );
}
